import { type RefObject, useEffect, useRef } from "react"

interface Options {
	/** Sentinel rendered at the visual TOP of the message list. */
	sentinelRef: RefObject<HTMLElement | null>
	enabled: boolean
	/** Re-arm key — pass the message count so consecutive pages keep firing
	 * while the sentinel stays in view after a load. */
	rearmKey: number
	onVisible: () => void
}

/**
 * Fires `onVisible` when the sentinel scrolls into view (with a generous
 * margin, so older messages start loading slightly before the user hits the
 * top). The companion of `useBottomVisibleTrigger`, pointed the other way:
 * infinite history loading instead of acknowledgment.
 */
export function useTopVisibleTrigger({
	sentinelRef,
	enabled,
	rearmKey,
	onVisible,
}: Options): void {
	const callbackRef = useRef(onVisible)
	useEffect(() => {
		callbackRef.current = onVisible
	})

	// biome-ignore lint/correctness/useExhaustiveDependencies: rearmKey deliberately re-creates the observer after each loaded page (see below).
	useEffect(() => {
		const el = sentinelRef.current
		if (!el || !enabled) return
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((e) => e.isIntersecting)) callbackRef.current()
			},
			{ rootMargin: "200px" },
		)
		observer.observe(el)
		return () => observer.disconnect()
		// rearmKey re-creates the observer after each loaded page so a sentinel
		// that is STILL visible (short history, tall viewport) fires again.
	}, [enabled, sentinelRef, rearmKey])
}
